/** How a worker's name is PRESENTED, for the ones we have a title for.
 *
 * Not a roster and not a default set — the install decides who exists, and this
 * only decides what they are called on screen. That is why an unrecognised repo is
 * neither dropped nor guessed at: it appears under the name it was created with,
 * because the install recorded creating it.
 *
 * Executors never appear here because they never appear in the recorded list:
 * they have no brain of their own. That is also the right reader-facing answer —
 * Ezra delegates to one, and that is the persona's business, not a second entity
 * to hand the founder. */
const WORKER_LABELS: Readonly<Record<string, string>> = {
  ezra: "Ezra - Azure Expert",
  stacey: "Stacey - Startup Advisor",
};

/** `acme/ezra-brain` → `ezra`. The worker's name is what the founder types. */
function workerNameOf(brainRepo: string): string {
  const leaf = brainRepo.slice(brainRepo.lastIndexOf("/") + 1);
  return leaf.endsWith("-brain") ? leaf.slice(0, -"-brain".length) : leaf;
}

/**
 * The end-of-install summary `m8t bootstrap status` prints when the install lands.
 * Pure (no I/O, no colour codes) so the copy is unit-tested directly.
 *
 * `brains` is WHAT THE INSTALL RECORDED CREATING — `doc.result.brains`, the list
 * the installer emits — and nothing else. It used to be a hardcoded roster gated
 * on "are there GitHub App credentials on this machine", which is a different
 * question with a different answer: a founder who ran `app-create` earlier, or
 * whose brain creation failed, was told their team included `<org>/ezra-brain`
 * while the very same status document said `brains: []`. Credentials on disk are
 * evidence that brains COULD be created, never that this install created any.
 *
 * Empty or absent → a basic platform: we claim no agents and point at
 * `m8t coder deploy` instead. Absent is treated as empty deliberately — an
 * installer old enough not to report its brains has told us nothing, and
 * nothing is not a licence to claim something.
 */
export function renderInstallSummary(args: { webappUrl?: string; brains?: readonly string[] }): string {
  const open = args.webappUrl ? `${args.webappUrl}        (or run: m8t open)` : "run: m8t open";
  const brains = args.brains ?? [];
  const lines: string[] = [
    "✅ Your m8t platform is ready to use.",
    `   Open it:  ${open}`,
    "",
  ];
  if (brains.length > 0) {
    const workers = brains.map((brain) => {
      const name = workerNameOf(brain);
      return { name, label: WORKER_LABELS[name] ?? name, brain };
    });
    lines.push("   Your team:");
    for (const w of workers) {
      lines.push(`     • ${w.label}   · brain: ${w.brain}`);
    }
    // The reach line names the workers that exist, for the same reason the rows
    // do: `@ezra` was hardcoded beside a roster that could not contain anyone else.
    const handles = workers.map((w) => `@${w.name} (or /${w.name})`).join(", ");
    lines.push(
      "",
      `   Reach them:  ${handles} in your coding agent`,
      "                · the webapp  · or connect Telegram in the webapp (Channels)",
    );
  } else {
    lines.push(
      "   No brain-backed workers yet — deploy one with:  m8t coder deploy <name>   (or the m8t-architect skill)",
      "   Reach your platform via the webapp, or `m8t open`.",
    );
  }
  return lines.join("\n") + "\n";
}
